/**
 * Client-side image compression using Canvas API.
 * Resizes to max dimension and exports as JPEG at specified quality.
 * Preserves readability for OCR while reducing file size ~80-90%.
 *
 * Typical results: 5MB phone photo → 200-400KB compressed JPEG.
 * Uses OffscreenCanvas where available, falls back to HTMLCanvasElement
 * for older iOS Safari (< 16.4).
 */

const MAX_DIMENSION = 1920; // px — sufficient for OCR on documents
const JPEG_QUALITY = 0.80; // 0-1, 0.80 = optimized for text readability on paper docs

/**
 * Compress an image file, returning a new File ready for upload.
 * - Resizes to fit within MAX_DIMENSION (maintaining aspect ratio)
 * - Converts PNG/WebP to JPEG (smaller for photos)
 * - If the image is already smaller than threshold, returns it unchanged
 */
export async function compressImage(file: File): Promise<File> {
	// Skip if already small enough (under 500KB)
	if (file.size <= 500 * 1024) {
		return file;
	}

	const bitmap = await createImageBitmap(file);
	const { width, height } = bitmap;

	// Calculate new dimensions (fit within MAX_DIMENSION box)
	let newWidth = width;
	let newHeight = height;

	if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
		if (width > height) {
			newWidth = MAX_DIMENSION;
			newHeight = Math.round(height * (MAX_DIMENSION / width));
		} else {
			newHeight = MAX_DIMENSION;
			newWidth = Math.round(width * (MAX_DIMENSION / height));
		}
	}

	let blob: Blob;

	if (typeof OffscreenCanvas !== 'undefined') {
		// Modern path: OffscreenCanvas (Chrome 69+, Firefox 105+, Safari 16.4+)
		const canvas = new OffscreenCanvas(newWidth, newHeight);
		const ctx = canvas.getContext('2d');
		if (!ctx) { bitmap.close(); return file; }
		ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
		blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
	} else {
		// Fallback: HTMLCanvasElement (older iOS Safari, etc.)
		const canvas = document.createElement('canvas');
		canvas.width = newWidth;
		canvas.height = newHeight;
		const ctx = canvas.getContext('2d');
		if (!ctx) { bitmap.close(); return file; }
		ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
		blob = await new Promise<Blob>((resolve) => {
			canvas.toBlob((b) => resolve(b ?? file), 'image/jpeg', JPEG_QUALITY);
		});
	}

	bitmap.close();

	// Create a new File with .jpg extension
	const baseName = file.name.replace(/\.[^.]+$/, '');
	return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}
